import type { PropertyType } from '@/backend/types'
import { useBilling } from '@/modules/Billing/useBilling'
import { useLimitedAction } from '@/modules/Billing/useLimitedAction'
import type { Property } from '@/modules/Project/Properties/types'
import { serializeProperty } from '@/modules/Project/serializer'
import { useProjectStore } from '@/modules/Project/useProjectStore'
import { useProperty } from '@/modules/Project/useProperty'
import { optimisticPropertyMap } from '@/modules/Project/utils'
import { useCurrentWorkspace } from '@/modules/Workspaces/useCurrentWorkspace'
import { ANALYTICS_EVENT, useAnalytics } from '@/sharedComposables/useAnalytics'
import pLimit from 'p-limit'
import { useProjects } from '../Projects/useProjects'

export function useProjectActions() {
  const workspace = useCurrentWorkspace()
  const projectStore = useProjectStore()
  const { addProperty } = useLimitedAction()
  const billingStore = useBilling()
  const propertyStore = useProperty()
  const { captureAnalyticsEvent } = useAnalytics()
  const projectsStore = useProjects()

  async function createPropertyFromType(
    workspaceId: string,
    projectId: string,
    type: PropertyType,
  ) {
    const optimisticId = crypto.randomUUID()
    const optimisticProperty: Property = {
      ...optimisticPropertyMap[type],
      id: optimisticId,
      slug: optimisticId,
    }

    return createPropertyFromObject(workspaceId, projectId, optimisticProperty)
  }

  async function createPropertyFromObject(
    workspaceId: string,
    projectId: string,
    property: Property,
  ): Promise<Property> {
    const optimisticId = property.id
    const optimisticProperty = property

    projectStore.optimisticIds.push(optimisticId)
    projectStore.upsertProperty(optimisticProperty)
    if (projectStore.activeView) {
      projectStore.upsertView({
        ...projectStore.activeView.view,
        propertyIds: [...(projectStore.activeView.view.propertyIds ?? []), optimisticProperty.id],
      })
    }

    if (optimisticProperty.type === 'reference') {
      const limit = pLimit(10)
      projectsStore.mainProjects.forEach((project) =>
        limit(() =>
          /**
           * This function returns a promise, but we don't need to wait for it because it's better
           * to show the optimistic property immediately. If the user is superhuman they might be able to
           * open the related project submenu before the limits are loaded, in which case they won't
           * see entity counts per project immediately - not a big deal.
           */
          billingStore.loadProjectLimits({ projectId: project.id, workspaceId }),
        ),
      )

      projectStore.upsertProperty(optimisticProperty)
      /**
       * Reference properties can't be created on the backend until the
       * user has linked a reference project. So we return before sending
       * the BE request. The property will be created on the backend
       * from a different component.
       */
      return optimisticProperty
    }

    const result = await addProperty({
      workspaceId,
      projectId,
      property: optimisticProperty,
      viewId: projectStore.activeView?.id ?? projectStore.mainView?.id,
    })

    projectStore.removeProperty(optimisticId)
    if (projectStore.activeView) {
      projectStore.upsertView({
        ...projectStore.activeView.view,
        propertyIds:
          projectStore.activeView.view.propertyIds?.filter((id) => id !== optimisticId) || null,
      })
    }

    if (!result.ok) {
      throw new Error('Failed to create property')
    }

    const newPropertyId = result.data.id
    if (
      // The websocket connection might have already added this property ID
      // to the view
      projectStore.activeView?.view.propertyIds &&
      !projectStore.activeView.view.propertyIds.includes(newPropertyId)
    ) {
      projectStore.activeView.view.propertyIds.push(newPropertyId)
    }

    if (billingStore.fieldUsage) {
      billingStore.fieldUsage.limitUsage += projectStore.mainView?.entities?.length ?? 0
    }

    const serializedProperty = serializeProperty(result.data)

    projectStore.upsertProperty(serializedProperty)

    propertyStore.reset()

    captureAnalyticsEvent(ANALYTICS_EVENT.PROPERTY_CREATED, {
      workspaceId: workspace.value.id,
      projectId: projectStore.projectId,
      propertyId: newPropertyId,
    })

    return serializedProperty
  }

  return {
    createPropertyFromType,
    createPropertyFromObject,
  }
}
